import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import SectionHeading from "./SectionHeading";
import { CASE_STUDIES } from "@/data/content";
import { Link } from "react-router-dom";

export const FeaturedWork = ({ limit = 4 }) => {
    const items = CASE_STUDIES.slice(0, limit);
    return (
        <section
            id="work"
            className="relative py-24 lg:py-32"
            aria-labelledby="work-heading"
        >
            <div className="mx-auto max-w-7xl container-px">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
                    <SectionHeading
                        eyebrow="Featured Work"
                        title={
                            <>
                                Systems we’ve built.{" "}
                                <span className="italic text-[hsl(var(--text-muted))]">Outcomes that compound.</span>
                            </>
                        }
                        subtitle="Selected projects from our partner network. Each engagement leaves an owned engine, agent stack, or growth system behind."
                    />
                    <Link
                        to="/work"
                        className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] transition-colors group"
                        data-testid="featured-work-view-all"
                    >
                        View all work
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                </div>
                <div
                    id="work-heading"
                    className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6"
                >
                    {items.map((c, i) => (
                        <motion.article
                            key={c.id}
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-10% 0px" }}
                            transition={{ duration: 0.55, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                            whileHover={{ y: -4 }}
                            data-testid="work-case-study-card"
                            className="group relative overflow-hidden rounded-3xl border border-black/[0.07] bg-white hover:border-black/15 hover:shadow-deep transition-all duration-300"
                        >
                            <div className="relative aspect-[16/10] overflow-hidden bg-[hsl(var(--bg-2))]">
                                <img
                                    src={c.cover}
                                    alt={`${c.client} case study cover`}
                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                                    loading="lazy"
                                />
                                <div
                                    className="absolute inset-0 pointer-events-none"
                                    style={{
                                        background:
                                            "linear-gradient(180deg, rgba(255,255,255,0) 50%, rgba(255,255,255,0.45) 100%)",
                                    }}
                                />
                                <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full glass-strong px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-[hsl(var(--text))]">
                                    {c.category}
                                </div>
                            </div>
                            <div className="p-6 lg:p-7">
                                <div className="text-[11.5px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                                    {c.client}
                                </div>
                                <h3 className="mt-2 font-display text-2xl lg:text-[28px] leading-tight text-[hsl(var(--text))]">
                                    {c.title}
                                </h3>
                                <p className="mt-3 text-[14px] text-[hsl(var(--text-muted))] leading-relaxed">
                                    {c.summary}
                                </p>
                                <div className="mt-5 flex flex-wrap gap-2">
                                    {c.metrics.map((m) => (
                                        <span
                                            key={m.label}
                                            className="inline-flex items-baseline gap-1.5 rounded-full bg-[hsl(var(--bg-2))] ring-1 ring-black/[0.06] px-3 py-1"
                                        >
                                            <span className="font-display text-[15px] text-[hsl(var(--gold))]">
                                                {m.value}
                                            </span>
                                            <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-[hsl(var(--text-subtle))]">
                                                {m.label}
                                            </span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default FeaturedWork;
